import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import keycloak, { configureKeycloak, lockKeycloak } from '../keycloak';
import getEnv from '../env';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userPersonEntity, setUserPersonEntity] = useState(null);
  // Registration approval status (#35): null while unknown/loading, then
  // 'pending' | 'approved' | 'rejected' from the profile upsert response.
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const didInit = useRef(false);

  useEffect(() => {
    // Prevent double initialization in StrictMode
    if (didInit.current) return;
    didInit.current = true;

    // Which realm and client this node signs in with comes from the backend
    // (/info `auth`), so one web deployment serves every node; the env values
    // in keycloak.js stand in when /info is unreachable or says nothing.
    const API_BASE = getEnv('REACT_APP_API_URL') || 'http://localhost:8000';
    fetch(`${API_BASE}/info`)
      .then((res) => (res.ok ? res.json() : null))
      .then((info) => configureKeycloak(info && info.auth))
      .catch(() => configureKeycloak(null))
      .then(() => {
        lockKeycloak();
        return keycloak.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
          pkceMethod: 'S256',
        });
      })
      .then((authenticated) => {
        setIsAuthenticated(authenticated);
        if (authenticated) {
          setToken(keycloak.token);
          const id = keycloak.tokenParsed?.sub;
          const username = keycloak.tokenParsed?.preferred_username;
          const email = keycloak.tokenParsed?.email;
          const realmRoles = keycloak.tokenParsed?.realm_access?.roles || [];
          const realmMgmtRoles = keycloak.tokenParsed?.resource_access?.['realm-management']?.roles || [];
          const roles = [...new Set([...realmRoles, ...realmMgmtRoles])];
          const groups = keycloak.tokenParsed?.groups || [];

          setUser({
            id,
            username,
            email,
            name: keycloak.tokenParsed?.name,
            roles,
            groups,
          });

          // Store in localStorage for easy access
          localStorage.setItem('id', id || '');
          localStorage.setItem('username', username || '');
          localStorage.setItem('email', email || '');
          localStorage.setItem('role', roles[0] || '');
          localStorage.setItem('groups', JSON.stringify(groups));
          localStorage.setItem('name', keycloak.tokenParsed?.name || '');

          // Ensure person entity exists for user and store it in context
          if (id) {
            const API_URL = getEnv('REACT_APP_API_URL') || 'http://localhost:8000';
            // Upsert user profile first — entities.user_id is a FK to user_profiles
            fetch(`${API_URL}/api/users/${id}/profile`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${keycloak.token}` },
              body: JSON.stringify({ name: keycloak.tokenParsed?.name, email: keycloak.tokenParsed?.email }),
            })
              .then(res => res.json())
              .then(profile => setRegistrationStatus(profile.registration_status || 'approved'))
              .catch(err => {
                console.error('Error upserting user profile:', err);
                // Fail open on a transient fetch error — don't strand the user on a loading screen.
                setRegistrationStatus('approved');
              })
              .finally(() => {
            fetch(`${API_URL}/api/entities?entity_type=person&user_id=${id}`)
              .then(res => res.json())
              .then(entities => {
                // Never mint one on the claim page. accept_claim binds the
                // *invited* person entity to this account and refuses outright
                // if the account already owns one, so a blank person created
                // here either wins the race and orphans the invitation, or
                // loses it and is never cleaned up. ClaimPage calls
                // refreshProfile() once the claim lands, which fills this in
                // with the claimed entity.
                const claiming = window.location.pathname.startsWith('/claim/');
                if (entities.length === 0 && !claiming) {
                  return fetch(`${API_URL}/api/entities`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      name: keycloak.tokenParsed?.name || 'My Profile',
                      entity_type: 'person',
                      user_id: id,
                    }),
                  }).then(res => res.json()).then(created => {
                    setUserPersonEntity(created);
                    return created;
                  });
                } else {
                  setUserPersonEntity(entities[0]);
                  return entities[0];
                }
              })
              .catch(err => console.error('Error ensuring person entity exists:', err));
              }); // end finally
          }
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Keycloak init error:', error);
        setIsLoading(false);
      });

    // Token refresh
    keycloak.onTokenExpired = () => {
      keycloak
        .updateToken(30)
        .then((refreshed) => {
          if (refreshed) {
            setToken(keycloak.token);
          }
        })
        .catch(() => {
          console.error('Failed to refresh token');
        });
    };
  }, []);

  const login = useCallback(() => {
    keycloak.login();
  }, []);

  // redirectUri: where Keycloak sends the browser afterwards (the claim page
  // passes its own URL to come straight back). Checked as a string because
  // several callers pass logout directly as an onClick handler, which hands
  // it the click event.
  const logout = useCallback((redirectUri) => {
    localStorage.removeItem('id');
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('role');
    localStorage.removeItem('groups');
    localStorage.removeItem('name');
    keycloak.logout({ redirectUri: typeof redirectUri === 'string' ? redirectUri : window.location.origin });
  }, []);

  const getToken = useCallback(() => {
    return keycloak.token;
  }, []);

  // Account deletion is password-gated (the backend verifies it against
  // Keycloak before previewing or deleting anything). The delete call streams
  // NDJSON progress — {type: 'plan'|'step'|'done'} — so the UI can tick off
  // each part as it's removed; see services/account_deletion.py.
  const accountRequest = useCallback(async (method, path, password) => {
    const API_URL = getEnv('REACT_APP_API_URL') || 'http://localhost:8000';
    const id = keycloak.tokenParsed?.sub;
    const response = await fetch(`${API_URL}/api/account/${id}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${keycloak.token}`,
      },
      body: JSON.stringify({ password }),
    });
    if (!response.ok) {
      let detail = 'Failed to delete account';
      try {
        const body = await response.json();
        if (body?.detail) detail = body.detail;
      } catch {
        /* no JSON body */
      }
      throw new Error(detail);
    }
    return response;
  }, []);

  const fetchDeletionPlan = useCallback(async (password) => {
    const response = await accountRequest('POST', '/deletion-plan', password);
    return response.json();
  }, [accountRequest]);

  // Resolves with the final `done` event; does NOT sign out, so the caller can
  // show the finished checklist first and then call signOutDeletedAccount().
  const deleteAccount = useCallback(async (password, onEvent = () => {}) => {
    const response = await accountRequest('DELETE', '', password);
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let done = null;
    while (true) {
      const { value, done: streamDone } = await reader.read();
      if (streamDone) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();
      for (const line of lines) {
        if (!line.trim()) continue;
        const event = JSON.parse(line);
        if (event.type === 'done') done = event;
        onEvent(event);
      }
    }
    if (!done) throw new Error('Connection closed before deletion finished');
    return done;
  }, [accountRequest]);

  const signOutDeletedAccount = useCallback(() => {
    localStorage.removeItem('id');
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('role');
    localStorage.removeItem('groups');
    localStorage.removeItem('name');
    keycloak.logout({ redirectUri: window.location.origin });
  }, []);

  // Re-read this user's profile: the local registration decision and the person
  // entity that belongs to them.
  //
  // Both are otherwise read exactly once, inside the didInit-guarded effect
  // above, so a server-side change during the session stays invisible until the
  // tab is reloaded. Claiming a profile is exactly such a change: accept_claim
  // flips a pending registration to approved and binds the invited person
  // entity (routers/entities.py). Without this the just-claimed user reaches
  // ProtectedRoute still carrying the 'pending' read one request earlier, and
  // is shown the onboarding screen asking them to wait for an admin who — by
  // issuing the claim link at all — already approved them.
  //
  // Leaves the last known values in place on a failed fetch rather than
  // stranding the caller; it is a refresh, not an authority.
  const refreshProfile = useCallback(async () => {
    const id = keycloak.tokenParsed?.sub;
    if (!id) return null;
    const API_URL = getEnv('REACT_APP_API_URL') || 'http://localhost:8000';
    const headers = { Authorization: `Bearer ${keycloak.token}` };
    let status = null;
    try {
      const res = await fetch(`${API_URL}/api/users/${id}/profile`, { headers });
      if (res.ok) {
        status = (await res.json()).registration_status || 'approved';
        setRegistrationStatus(status);
      }
    } catch {
      /* keep the status we already have */
    }
    try {
      const res = await fetch(`${API_URL}/api/entities?entity_type=person&user_id=${id}`, { headers });
      if (res.ok) {
        const entities = await res.json();
        if (entities.length > 0) setUserPersonEntity(entities[0]);
      }
    } catch {
      /* keep the person entity we already have */
    }
    return status;
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    token,
    userPersonEntity,
    registrationStatus,
    login,
    logout,
    getToken,
    refreshProfile,
    deleteAccount,
    fetchDeletionPlan,
    signOutDeletedAccount,
    keycloak,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
